import { useEffect, useRef, useState } from "react";
import * as HeroIcons from '@heroicons/react/24/outline';
import { Button } from "./button";
import { Shipment } from "@/pages/shipments/in-transit/columns";

export const MobileDetailsPopover: React.FC<{ shipment: Shipment }> = ({ shipment }) => {
    const [isOpen, setIsOpen] = useState(false);
    const popoverRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const isDisabled = !shipment.driver_mobile && !shipment.vendor_mobile;

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target as Node) &&
                buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    const driverMobile = shipment.driver_mobile || 'Not available';
    const vendorMobile = shipment.vendor_mobile || 'Not available';
    const driverName = shipment.driver_name || "Not available";

    return (
        <div className="relative inline-block ml-2">
            <Button
                ref={buttonRef}
                variant="outline" 
                size="sm" 
                disabled={isDisabled} 
                className={`h-7 px-2.5 py-1 text-xs transition-colors duration-150 ${
                    isDisabled
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                        : 'bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200 hover:text-blue-800'
                }`}
                onClick={() => setIsOpen(!isOpen)}
            >
                <HeroIcons.PhoneIcon className="h-4 w-4" />
            </Button>

            {isOpen && (
                <div 
                    ref={popoverRef}
                    className="absolute z-20 top-1/2 right-full mr-3 transform -translate-y-1/2
                               w-56 p-4 rounded-xl shadow-2xl bg-white border border-gray-100/70 
                               ring-1 ring-gray-900/5 backdrop-blur-sm"
                >
                    <h4 className="text-sm font-bold mb-2 text-gray-900 border-b pb-1">
                        <HeroIcons.ChatBubbleLeftRightIcon className="inline h-4 w-4 mr-1 text-blue-500" />
                        Contact Info
                    </h4>
                    <div className="text-xs space-y-2">
                        <div>
                            <p className="flex justify-between items-center">
                                <span className="font-medium text-gray-600">Driver Name:</span>
                                <span className={`${shipment.driver_name ? 'text-gray-900 font-semibold' : 'text-red-500 italic'}`}>
                                    {driverName}
                                </span>
                            </p>
                            <p className="flex justify-between items-center mt-1">
                                <span className="font-medium text-gray-600">Driver Mobile:</span>
                                <span className={`${shipment.driver_mobile ? 'text-gray-900 font-semibold' : 'text-red-500 italic'}`}>
                                    {driverMobile}
                                </span>
                            </p>
                        </div>
                        <p className="flex justify-between items-center pt-1 border-t border-gray-100 mt-2">
                            <span className="font-medium text-gray-600">Vendor:</span>
                            <span className={`${shipment.vendor_mobile ? 'text-gray-900 font-semibold' : 'text-red-500 italic'}`}>
                                {vendorMobile}
                            </span>
                        </p>
                    </div>
                    <div className="absolute top-1/2 left-full transform -translate-y-1/2 w-0 h-0 
                                    border-y-8 border-y-transparent border-l-8 border-l-white 
                                    shadow-xl"></div>
                </div>
            )}
        </div>
    );
};